import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { FormEvent, useState } from 'react';
import axios from 'axios';
import { optionsFetcher, useBasePath } from './utils/utilities';
import {
    ChoiceField,
    EmailField,
    FileUploadField,
    StringField,
    Errors,
} from './types/form';
import usePageView from './hooks/usePageView';

type LeadOptions = {
    actions: {
        POST: Post;
    };
};

type Post = {
    attachment: FileUploadField;
    city: StringField;
    email: EmailField;
    first_name: StringField;
    last_name: StringField;
    notes: StringField;
    phone_number: StringField;
    urgency: ChoiceField;
    street: StringField;
    zip: StringField;
};

type FormData = {
    first_name: string;
    last_name: string;
    street: string;
    house_number: string;
    zip: string;
    city: string;
    email: string;
    phone_number: string;
    notes: string;
    submission: string;
    product: number;
    urgency: string;
};

export default function Offer() {
    const [errors, setErrors] = useState<Errors>(undefined);
    const { id } = useParams();

    const [formData, setFormData] = useState<FormData>({
        first_name: '',
        last_name: '',
        street: '',
        house_number: '',
        zip: '',
        city: '',
        email: '',
        phone_number: '',
        notes: '',
        submission: localStorage.getItem('uuid') || '',
        product: id ? parseInt(id, 10) : -1,
        urgency: '',
    });

    const { data, isLoading } = useSWR<LeadOptions>('/lead/', optionsFetcher);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const basePath = useBasePath();

    usePageView();

    async function handleSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        try {
            await axios.post(`lead/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            localStorage.removeItem('uuid');
            localStorage.removeItem('answers');

            window.location.href = `${basePath}/offer/thankyou`;
        } catch (error: any) {
            if (
                error?.response?.data &&
                typeof error?.response?.data === 'object'
            ) {
                const responseErrors = Object.entries(error.response?.data).map(
                    ([key, value]) => {
                        return `${key}: ${value}`;
                    }
                );
                setErrors(responseErrors);
            } else {
                setErrors([t('Something went wrong')]);
            }
        }
    }

    if (!id) {
        return <div>{t('Something went wrong')}</div>;
    }
    if (isLoading || !data) return <div>Loading...</div>;

    return (
        <div className="advices-suisse">
            <h2 className="beta-heading advices-suisse__main-title">
                {t('Request free quotation')}
            </h2>

            <div className="advices-suisse__links">
                <button
                    className="advices-suisse__back-button"
                    type={'button'}
                    onClick={() => navigate(`${basePath}/results/`)}
                >
                    {t('< Back')}
                </button>
            </div>

            <div className="offerintro">
                <p className="paragraph">
                    {t(
                        "After requesting your free quotation, you will be contacted as soon as possible by a 'Qualified Innovation Partner' from Vaillant."
                    )}
                </p>
                <p className="paragraph">
                    {t(
                        'These exclusively selected installers, under the guidance of Vaillant, have achieved the highest possible certifications, guaranteeing you a professional partner for the installation of your heat pump system.'
                    )}
                </p>
            </div>

            <form
                className="offerform js-form-validate"
                onSubmit={handleSubmit}
            >
                <div className="offerform__duo">
                    <input
                        type="text"
                        name={'first_name'}
                        id={'first_name'}
                        required={data.actions.POST.first_name.required}
                        maxLength={data.actions.POST.first_name.max_length}
                        placeholder={data.actions.POST.first_name.label}
                        defaultValue={formData.first_name}
                        onChange={(e) =>
                            setFormData((prev) => {
                                return { ...prev, first_name: e.target.value };
                            })
                        }
                    />
                    <input
                        type="text"
                        name={'last_name'}
                        id={'last_name'}
                        required={data.actions.POST.last_name.required}
                        maxLength={data.actions.POST.last_name.max_length}
                        placeholder={data.actions.POST.last_name.label}
                        defaultValue={formData.last_name}
                        onChange={(e) =>
                            setFormData((prev) => {
                                return { ...prev, last_name: e.target.value };
                            })
                        }
                    />
                </div>
                <div className="offerform__duo offerform__duo--first-small">
                    <input
                        type="text"
                        name={'street'}
                        id={'street'}
                        required={data.actions.POST.street.required}
                        maxLength={data.actions.POST.street.max_length}
                        placeholder={data.actions.POST.street.label}
                        defaultValue={formData.street}
                        onChange={(e) =>
                            setFormData((prev) => {
                                return { ...prev, street: e.target.value };
                            })
                        }
                    />
                </div>
                <div className="offerform__duo offerform__duo--first-small">
                    <input
                        type="text"
                        name={'zip'}
                        id={'zip'}
                        required={data.actions.POST.zip.required}
                        maxLength={data.actions.POST.zip.max_length}
                        placeholder={data.actions.POST.zip.label}
                        defaultValue={formData.zip}
                        onChange={(e) =>
                            setFormData((prev) => {
                                return { ...prev, zip: e.target.value };
                            })
                        }
                    />
                    <input
                        type="text"
                        name={'city'}
                        id={'city'}
                        required={data.actions.POST.city.required}
                        maxLength={data.actions.POST.city.max_length}
                        placeholder={data.actions.POST.city.label}
                        defaultValue={formData.city}
                        onChange={(e) =>
                            setFormData((prev) => {
                                return { ...prev, city: e.target.value };
                            })
                        }
                    />
                </div>

                <input
                    type="email"
                    name={'email'}
                    id={'email'}
                    required={data.actions.POST.email.required}
                    maxLength={data.actions.POST.email.max_length}
                    placeholder={data.actions.POST.email.label}
                    defaultValue={formData.email}
                    onChange={(e) =>
                        setFormData((prev) => {
                            return { ...prev, email: e.target.value };
                        })
                    }
                />
                <input
                    type="tel"
                    name={'phone_number'}
                    id={'phone_number'}
                    required={data.actions.POST.phone_number.required}
                    maxLength={data.actions.POST.phone_number.max_length}
                    placeholder={data.actions.POST.phone_number.label}
                    defaultValue={formData.phone_number}
                    onChange={(e) =>
                        setFormData((prev) => {
                            return { ...prev, phone_number: e.target.value };
                        })
                    }
                />
                <input
                    type="text"
                    name={'notes'}
                    id={'notes'}
                    required={data.actions.POST.notes.required}
                    maxLength={data.actions.POST.notes.max_length}
                    placeholder={data.actions.POST.notes.label}
                    defaultValue={formData.notes}
                    onChange={(e) =>
                        setFormData((prev) => {
                            return { ...prev, notes: e.target.value };
                        })
                    }
                />

                <div className="select-container">
                    <select
                        id="button-urgency"
                        name="urgency"
                        defaultValue={formData.urgency}
                        required={data.actions.POST.urgency.required}
                        onChange={(e) =>
                            setFormData((prev) => {
                                return { ...prev, urgency: e.target.value };
                            })
                        }
                    >
                        {data.actions.POST.urgency.choices.map((choice) => (
                            <option value={choice.value} key={choice.value}>
                                {choice.display_name}
                            </option>
                        ))}
                    </select>
                </div>

                <input
                    type="file"
                    id={'attachment'}
                    name={'attachment'}
                    className={'input custom-fileinput'}
                    onChange={(e) => {
                        setFormData((prev) => {
                            if (!e?.target?.files?.[0]) {
                                setErrors([
                                    t(
                                        'Something went wrong with uploading the file.'
                                    ),
                                ]);
                                return prev;
                            }

                            return {
                                ...prev,
                                attachment: e.target.files[0],
                            };
                        });
                    }}
                />

                <p className="paragraph paragraph-light">
                    {t(
                        'By clicking on the "Send" button, I agree that Vaillant GmbH, based in Riedstrasse 12, 8953 CH-Dietikon, can contact me by telephone and e-mail in order to process my request. I also acknowledge that my personal data will be stored and processed for these purposes. The consent can be revoked at any time with effect for the future. Please note our general '
                    )}
                    <a
                        href="https://www.vaillant.ch/"
                        className="link"
                        target="_blank"
                        rel={'noreferrer'}
                    >
                        {t('data protection regulations')}
                    </a>
                    .
                </p>

                {errors && (
                    <div className="errors">
                        {errors.map((error) => {
                            return <div key={error}>{error}</div>;
                        })}
                    </div>
                )}

                <button
                    type="submit"
                    className="form-navigation__button form-navigation__button--next form-navigation__button--submit"
                >
                    {t('Send')}
                </button>
            </form>
        </div>
    );
}
